import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, KeyRound } from 'lucide-react';
import { apiChangePassword, apiMe } from '../api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const ChangePasswordModal = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isFirstLogin = !!user?.must_change_password;

  if (!isFirstLogin) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) { toast.error(t('auth.changePassword.tooShort')); return; }
    if (newPwd !== confirmPwd) { toast.error(t('auth.changePassword.mismatch')); return; }
    setSubmitting(true);
    try {
      await apiChangePassword(currentPwd, newPwd);
      toast.success(t('auth.changePassword.success'));
      await apiMe();
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} modal={true}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </div>
          </div>
          <DialogTitle>{t('auth.changePassword.title')}</DialogTitle>
          <DialogDescription>
            {t('auth.changePassword.subtitle')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="currpwd">{t('auth.changePassword.currentPassword')}</Label>
            <Input id="currpwd" type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} autoComplete="current-password" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="newpwd">{t('auth.changePassword.newPassword')} <span className="text-red-500">*</span></Label>
            <Input id="newpwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} autoComplete="new-password" required />
            <div className="text-[11px] text-slate-500">{t('auth.changePassword.requirements')}</div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmpwd">{t('auth.changePassword.confirmPassword')} <span className="text-red-500">*</span></Label>
            <Input id="confirmpwd" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} autoComplete="new-password" required />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} className="w-full bg-blue-700 hover:bg-blue-800 gap-2">
              <KeyRound className="w-4 h-4" />
              {submitting ? t('auth.changePassword.submitting') : t('auth.changePassword.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordModal;
